/**
 * MyCopro - Hook usePermissions
 * Gestion des permissions selon la catégorie utilisateur
 * ET le mode de la résidence (bootstrap vs active)
 * 
 * MODE BOOTSTRAP (hasActiveCouncil: false):
 * - Tous les membres sont auto-validés à l'inscription
 * - Locataires et copropriétaires peuvent inviter
 * - Copropriétaires peuvent devenir membre du CS (fin du bootstrap)
 * 
 * MODE ACTIF (hasActiveCouncil: true):
 * - Nouvelles inscriptions requièrent validation du CS
 * - Seuls owners/council/syndic/admin peuvent inviter
 */

import { useMemo } from 'react';
import { useAuthStore, useResidenceStore } from '@/stores';
import { UserCategoryType } from '@/types';

export interface Permissions {
  // Catégorie
  category: UserCategoryType;
  
  // Rôles
  isAdmin: boolean;
  isSyndic: boolean;
  isCouncil: boolean;
  isOwner: boolean;
  isTenant: boolean;
  isGardien: boolean;
  
  // Accès
  hasAGAccess: boolean;
  
  // Permissions générales
  canManage: boolean;
  canInvite: boolean;
  
  // Permissions spécifiques
  canValidateRequests: boolean;
  canValidateInvitations: boolean;
  canManageUsers: boolean;
  canManagePolls: boolean;
  canManageDocuments: boolean;
  canSendNotifications: boolean;
  canEditResidenceSettings: boolean;
  canAccessChat: boolean;
  
  // Chat access
  chatAccess: ('residence' | 'owners' | 'council')[];
  
  // ============================================================
  // MODE BOOTSTRAP - Nouvelles permissions
  // ============================================================
  
  /**
   * Indique si la résidence est en mode bootstrap (pas de CS actif)
   */
  isBootstrapMode: boolean;
  
  /**
   * Peut inviter des membres en mode bootstrap
   */
  canInviteInBootstrap: boolean;
  
  /**
   * Peut créer des demandes (bloqué en bootstrap pour non-managers)
   */
  canCreateRequests: boolean;
  
  /**
   * Peut devenir membre du conseil syndical (copropriétaire en mode bootstrap)
   */
  canBecomeCouncil: boolean;

  canValidateUsers: boolean;
  canManageBailleurs: boolean;
}

export const usePermissions = (): Permissions => {
  const currentUser = useAuthStore(state => state.currentUser);
  const currentResidence = useResidenceStore(state => state.currentResidence);

  return useMemo(() => {
    const category: UserCategoryType = currentUser?.category || 'tenant';

    // Rôles de base
    const isAdmin = category === 'admin';
    const isSyndic = category === 'syndic';
    const isCouncil = category === 'council';
    const isOwner = category === 'owner';
    const isTenant = category === 'tenant';
    const isGardien = category === 'gardien';

    // Accès AG (propriétaires et CS)
    const hasAGAccess = isAdmin || isSyndic || isCouncil || isOwner;

    // Mode bootstrap : pas de CS actif dans la résidence
    const isBootstrapMode = currentResidence ? !currentResidence.hasActiveCouncil : false;

    const isManager = isAdmin || isCouncil || isSyndic;
    const hasFullAccess = isManager || !isBootstrapMode;

    // Permissions étendues du gardien (lues depuis gardienSettings de la résidence)
    const gardienSettings = isGardien ? (currentResidence as any)?.gardienSettings : null;

    // Permissions de gestion
    const canManage = isAdmin || isCouncil;
    const canInvite = isAdmin || isCouncil || isSyndic || isOwner || (isTenant && isBootstrapMode);

    // Permissions spécifiques — gardien peut obtenir des droits étendus via les toggles CS
    const canValidateRequests = isAdmin || isCouncil || (isGardien && gardienSettings?.canValidateRequests === true);
    const canValidateInvitations = isAdmin || isCouncil || (isGardien && gardienSettings?.canValidateInscriptions === true);
    const canManageUsers = isAdmin || isCouncil;
    const canManagePolls = isAdmin || isCouncil;
    const canManageDocuments = isAdmin || isCouncil || isSyndic;
    const canSendNotifications = isAdmin || isCouncil || isSyndic || (isGardien && gardienSettings?.canSendNotifications === true);
    const canEditResidenceSettings = isAdmin || isCouncil;
    const canAccessChat = !isSyndic;

    // Accès chat par groupe
    let chatAccess: ('residence' | 'owners' | 'council')[] = [];
    if (isAdmin || isCouncil) {
      chatAccess = ['residence', 'owners', 'council'];
    } else if (isOwner) {
      chatAccess = ['residence', 'owners'];
    } else if (isTenant) {
      chatAccess = ['residence'];
    } else if (isGardien) {
      // Accès chat gardien configuré par les toggles CS
      if (gardienSettings?.chatResidence) chatAccess.push('residence');
      if (gardienSettings?.chatOwners) chatAccess.push('owners');
      if (gardienSettings?.chatCouncil) chatAccess.push('council');
    }

    // Permissions bootstrap
    const canInviteInBootstrap = isBootstrapMode && (isOwner || isTenant || isManager);
    const canCreateRequests = hasFullAccess && (isTenant || isOwner || isCouncil || isSyndic || isGardien);
    const canBecomeCouncil = isOwner && isBootstrapMode;

    return {
      category,
      isAdmin,
      isSyndic,
      isCouncil,
      isOwner,
      isTenant,
      isGardien,
      hasAGAccess,
      canManage,
      canInvite,
      canValidateRequests,
      canValidateInvitations,
      canManageUsers,
      canManagePolls,
      canManageDocuments,
      canSendNotifications,
      canEditResidenceSettings,
      canAccessChat,
      chatAccess,

      // Bootstrap
      isBootstrapMode,
      canInviteInBootstrap,
      canCreateRequests,
      canBecomeCouncil,

      canValidateUsers: isAdmin || isCouncil,
      canManageBailleurs: isAdmin || isCouncil
    };
  }, [currentUser?.category, currentResidence?.setupMode, currentResidence?.hasActiveCouncil, (currentResidence as any)?.gardienSettings]);
};

/**
 * Fonction utilitaire pour calculer les permissions (sans hook)
 * Utile pour les composants serveur ou les tests
 */
export const calculatePermissions = (
  userCategory: UserCategoryType | undefined,
  residenceSetupMode: 'bootstrap' | 'active' | undefined,
  residenceHasActiveCouncil: boolean | undefined
): Permissions => {
  const category: UserCategoryType = userCategory || 'tenant';
  
  const isAdmin = category === 'admin';
  const isSyndic = category === 'syndic';
  const isCouncil = category === 'council';
  const isOwner = category === 'owner';
  const isTenant = category === 'tenant';
  const isGardien = category === 'gardien';
  
  const hasAGAccess = isAdmin || isSyndic || isCouncil || isOwner;
  
  // ✅ SIMPLIFIÉ : seul hasActiveCouncil compte
  const isBootstrapMode = !residenceHasActiveCouncil;
  const isManager = isAdmin || isCouncil || isSyndic;
  const hasFullAccess = isManager || !isBootstrapMode;
  
  const canManage = isAdmin || isCouncil;
  // ✅ En mode bootstrap, tenants peuvent aussi inviter
  const canInvite = isAdmin || isCouncil || isSyndic || isOwner || (isTenant && isBootstrapMode);
  
  const canValidateRequests = isAdmin || isCouncil;
  const canValidateInvitations = isAdmin || isCouncil;
  const canManageUsers = isAdmin || isCouncil;
  const canManagePolls = isAdmin || isCouncil;
  const canManageDocuments = isAdmin || isCouncil || isSyndic;
  const canSendNotifications = isAdmin || isCouncil || isSyndic;
  const canEditResidenceSettings = isAdmin || isCouncil;
  const canAccessChat = !isSyndic;
  
  let chatAccess: ('residence' | 'owners' | 'council')[] = [];
  if (isAdmin || isCouncil) {
    chatAccess = ['residence', 'owners', 'council'];
  } else if (isOwner) {
    chatAccess = ['residence', 'owners'];
  } else if (isTenant || isGardien) {
    chatAccess = ['residence'];
  }
  
  const canInviteInBootstrap = isBootstrapMode && (isOwner || isTenant || isManager);
  const canCreateRequests = hasFullAccess && (isTenant || isOwner || isCouncil || isSyndic || isGardien);
  const canBecomeCouncil = isOwner && isBootstrapMode;

  return {
    category,
    isAdmin,
    isSyndic,
    isCouncil,
    isOwner,
    isTenant,
    isGardien,
    hasAGAccess,
    canManage,
    canInvite,
    canValidateRequests,
    canValidateInvitations,
    canManageUsers,
    canManagePolls,
    canManageDocuments,
    canSendNotifications,
    canEditResidenceSettings,
    canAccessChat,
    chatAccess,
    isBootstrapMode,
    canInviteInBootstrap,
    canCreateRequests,
    canBecomeCouncil,
    canValidateUsers: isAdmin || isCouncil,
    canManageBailleurs: isAdmin || isCouncil
  };
};

/**
 * Hook pour vérifier une permission spécifique
 */
export const useHasPermission = (permission: keyof Permissions): boolean => {
  const permissions = usePermissions();
  return permissions[permission] as boolean;
};

export default usePermissions;
